// Comprehensive inline editors for the admin Setup Progress dialog.
// Mirrors the full Setup-page forms (English + Odia paired fields):
//   • ProfileEditor   — inline (grid 2-col, all profile fields)
//   • EnrolmentEditor — inline (4 numeric inputs + live total)
//   • VendorsEditor   — read-only table + sub-Dialog for add/edit/delete
//   • CchsEditor      — read-only table + sub-Dialog for add/edit/delete
//
// Vendors/CCHs use a SUB-DIALOG (not inline rows) because each record has
// 15+ fields and cramming them into a table row is unusable. The sub-dialog
// renders on top of the parent Setup Progress dialog (z-[95]) so the parent
// never closes — fulfilling the user's "no popup close, no navigation" ask.

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Save, X, Plus, Trash2, Pencil, Wallet,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import RelationPicker from "@/components/poshan/RelationPicker";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -------- shared field configs (mirror Setup.jsx) --------
const VENDOR_CATEGORIES = [
  { value: "grocery", label: "Grocery" },
  { value: "vegetable", label: "Vegetable" },
  { value: "egg", label: "Egg" },
  { value: "fuel", label: "Fuel" },
  { value: "ladoo", label: "Ladoo" },
  { value: "other", label: "Other" },
];
const CCH_CATEGORIES = [
  { value: "", label: "—" },
  { value: "SC", label: "SC" }, { value: "ST", label: "ST" },
  { value: "OBC", label: "OBC" }, { value: "GEN", label: "GEN" },
];
const CCH_GENDERS = [
  { value: "", label: "—" },
  { value: "M", label: "Male (M)" }, { value: "F", label: "Female (F)" },
];
const CCH_MODES = [
  { value: "Bank", label: "Bank" }, { value: "Cash", label: "Cash" },
  { value: "UPI", label: "UPI" }, { value: "Other", label: "Other" },
];

const VENDOR_EMPTY = {
  name: "", nameOdia: "", category: "grocery",
  fatherName: "", fatherNameOdia: "",
  village: "", villageOdia: "",
  post: "", postOdia: "",
  thana: "", thanaOdia: "",
  block: "", blockOdia: "",
  district: "", districtOdia: "",
  pincode: "", pincodeOdia: "",
  address: "", addressOdia: "",
  phone: "",
};

const CCH_EMPTY = {
  name: "", nameOdia: "",
  fatherName: "", fatherNameOdia: "",
  village: "", villageOdia: "",
  post: "", postOdia: "",
  thana: "", thanaOdia: "",
  block: "", blockOdia: "",
  district: "", districtOdia: "",
  pincode: "", pincodeOdia: "",
  address: "", addressOdia: "",
  gender: "", category: "",
  bankAccount: "", ifsc: "", phone: "",
  monthlyHonorarium: 0, defaultMode: "Bank",
};

function FieldLabel({ children }) {
  return <label className="mb-1 block text-[0.62rem] font-semibold uppercase tracking-wide text-ink-700">{children}</label>;
}

function PairField({ label, valueEn, valueOdia, onChangeEn, onChangeOdia, testIdEn, testIdOdia, required, type = "text" }) {
  return (
    <>
      <div>
        <FieldLabel>{label} (English){required && <span className="text-rose-600"> *</span>}</FieldLabel>
        <Input
          type={type}
          value={valueEn ?? ""}
          onChange={(e) => onChangeEn(e.target.value)}
          data-testid={testIdEn}
          className="h-8 text-xs"
        />
      </div>
      <div>
        <FieldLabel>{label} (ଓଡ଼ିଆ){required && <span className="text-rose-600"> *</span>}</FieldLabel>
        <Input
          value={valueOdia ?? ""}
          onChange={(e) => onChangeOdia(e.target.value)}
          data-testid={testIdOdia}
          className="h-8 text-xs font-[var(--font-odia)]"
        />
      </div>
    </>
  );
}

// ============================================================
// PROFILE EDITOR — full Setup-page parity (English + Odia paired).
// ============================================================
export function ProfileEditor({ details, onCancel, onSaved }) {
  const profile = details?.profile || {};
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      schoolName: profile.schoolName || "",
      schoolNameOdia: profile.schoolNameOdia || "",
      udiseCode: profile.udiseCode || "",
      category: profile.category || profile.schoolCategory || "",
      district: profile.district || "",
      districtOdia: profile.districtOdia || "",
      block: profile.block || "",
      blockOdia: profile.blockOdia || "",
      village: profile.village || "",
      villageOdia: profile.villageOdia || "",
      post: profile.post || "",
      postOdia: profile.postOdia || "",
      thana: profile.thana || "",
      thanaOdia: profile.thanaOdia || "",
      pincode: profile.pincode || "",
      pincodeOdia: profile.pincodeOdia || "",
      departmentName: profile.departmentName || "School & Mass Education Department",
      schemeName: profile.schemeName || "PM POSHAN",
    });
  }, [profile]);

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.schoolName?.trim()) { toast.error("School Name (English) is required"); return; }
    if (!profile.id) { toast.error("Profile id missing"); return; }
    setSaving(true);
    try {
      // Merge existing record (preserves enrolment counts, declared* fields,
      // setupCountsLockedAt, etc.) with edits, then POST. The backend uses
      // replace_one so we MUST send every field we want to keep.
      const payload = { ...profile, ...form };
      await axios.post(`${API}/school-profiles?id=${encodeURIComponent(profile.id)}`, payload);
      toast.success("School profile updated");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save profile");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-clay/40 bg-white p-3" data-testid="profile-editor">
      <p className="mb-2 text-[0.65rem] font-semibold uppercase tracking-wide text-clay">School profile · ସ୍କୁଲ୍ ପ୍ରୋଫାଇଲ୍</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <PairField label="School Name" required
          valueEn={form.schoolName} valueOdia={form.schoolNameOdia}
          onChangeEn={upd("schoolName")} onChangeOdia={upd("schoolNameOdia")}
          testIdEn="profile-edit-schoolName" testIdOdia="profile-edit-schoolNameOdia" />
        <div>
          <FieldLabel>UDISE Code</FieldLabel>
          <Input value={form.udiseCode || ""} disabled className="h-8 cursor-not-allowed bg-sand-100 text-xs" data-testid="profile-edit-udise" />
        </div>
        <div>
          <FieldLabel>School Category</FieldLabel>
          <Input value={form.category || ""} onChange={(e) => upd("category")(e.target.value)} placeholder="e.g. BV+Primary+Upper Primary" className="h-8 text-xs" data-testid="profile-edit-category" />
        </div>
        <PairField label="District"
          valueEn={form.district} valueOdia={form.districtOdia}
          onChangeEn={upd("district")} onChangeOdia={upd("districtOdia")}
          testIdEn="profile-edit-district" testIdOdia="profile-edit-districtOdia" />
        <PairField label="Block"
          valueEn={form.block} valueOdia={form.blockOdia}
          onChangeEn={upd("block")} onChangeOdia={upd("blockOdia")}
          testIdEn="profile-edit-block" testIdOdia="profile-edit-blockOdia" />
        <PairField label="Village"
          valueEn={form.village} valueOdia={form.villageOdia}
          onChangeEn={upd("village")} onChangeOdia={upd("villageOdia")}
          testIdEn="profile-edit-village" testIdOdia="profile-edit-villageOdia" />
        <PairField label="Post"
          valueEn={form.post} valueOdia={form.postOdia}
          onChangeEn={upd("post")} onChangeOdia={upd("postOdia")}
          testIdEn="profile-edit-post" testIdOdia="profile-edit-postOdia" />
        <PairField label="Thana / Police Station"
          valueEn={form.thana} valueOdia={form.thanaOdia}
          onChangeEn={upd("thana")} onChangeOdia={upd("thanaOdia")}
          testIdEn="profile-edit-thana" testIdOdia="profile-edit-thanaOdia" />
        <PairField label="Pincode"
          valueEn={form.pincode} valueOdia={form.pincodeOdia}
          onChangeEn={upd("pincode")} onChangeOdia={upd("pincodeOdia")}
          testIdEn="profile-edit-pincode" testIdOdia="profile-edit-pincodeOdia" />
        <div>
          <FieldLabel>Department</FieldLabel>
          <Input value={form.departmentName || ""} onChange={(e) => upd("departmentName")(e.target.value)} className="h-8 text-xs" data-testid="profile-edit-department" />
        </div>
        <div>
          <FieldLabel>Scheme</FieldLabel>
          <Input value={form.schemeName || ""} onChange={(e) => upd("schemeName")(e.target.value)} className="h-8 text-xs" data-testid="profile-edit-scheme" />
        </div>
      </div>
      <div className="mt-3 flex justify-end gap-2 border-t border-border pt-3">
        <Button variant="outline" size="sm" onClick={onCancel} className="h-7 text-xs" data-testid="profile-edit-cancel">
          <X className="mr-1 h-3.5 w-3.5" /> Cancel
        </Button>
        <Button size="sm" onClick={save} disabled={saving} className="h-7 bg-clay text-xs text-white hover:bg-clay-dark" data-testid="profile-edit-save">
          <Save className="mr-1 h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// ENROLMENT EDITOR — 4 numeric inputs + live total.
// ============================================================
export function EnrolmentEditor({ details, onCancel, onSaved }) {
  const profile = details?.profile || {};
  const [form, setForm] = useState({
    balVatikaEnrollment: profile.balVatikaEnrollment || 0,
    primaryEnrollment: profile.primaryEnrollment || 0,
    upperPrimaryEnrollment: profile.upperPrimaryEnrollment || 0,
    highSchoolEnrollment: profile.highSchoolEnrollment || 0,
  });
  const total = Number(form.balVatikaEnrollment) + Number(form.primaryEnrollment)
    + Number(form.upperPrimaryEnrollment) + Number(form.highSchoolEnrollment);
  const [saving, setSaving] = useState(false);

  const setN = (k) => (e) => {
    const v = Math.max(0, parseInt(e.target.value || "0", 10) || 0);
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async () => {
    if (!profile.id) { toast.error("Profile id missing"); return; }
    setSaving(true);
    try {
      const payload = { ...profile, ...form };
      await axios.post(`${API}/school-profiles?id=${encodeURIComponent(profile.id)}`, payload);
      toast.success("Enrolment updated");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save enrolment");
    } finally { setSaving(false); }
  };

  const rows = [
    ["Balvatika", "balVatikaEnrollment"],
    ["Primary (I–V)", "primaryEnrollment"],
    ["Upper Primary (VI–VIII)", "upperPrimaryEnrollment"],
    ["High School (IX–X)", "highSchoolEnrollment"],
  ];

  return (
    <div className="overflow-hidden rounded-lg border border-clay/40 bg-white" data-testid="enrolment-editor">
      <table className="w-full text-xs">
        <thead className="bg-sand-100 text-[0.65rem] uppercase tracking-wide text-ink-700">
          <tr>
            <th className="px-3 py-2 text-left">Class group</th>
            <th className="px-3 py-2 text-right">Students</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map(([label, key]) => (
            <tr key={key}>
              <td className="px-3 py-1.5">{label}</td>
              <td className="px-3 py-1.5 text-right">
                <Input
                  type="number"
                  min="0"
                  value={form[key]}
                  onChange={setN(key)}
                  className="ml-auto h-8 w-24 text-right text-xs"
                  data-testid={`enrolment-edit-${key}`}
                />
              </td>
            </tr>
          ))}
          <tr className="bg-emerald-50">
            <td className="px-3 py-1.5 font-bold">Total</td>
            <td className="px-3 py-1.5 text-right font-bold text-emerald-700" data-testid="enrolment-edit-total">{total}</td>
          </tr>
        </tbody>
      </table>
      <div className="flex justify-end gap-2 border-t border-border bg-sand-50 px-3 py-2">
        <Button variant="outline" size="sm" onClick={onCancel} className="h-7 text-xs" data-testid="enrolment-edit-cancel">
          <X className="mr-1 h-3.5 w-3.5" /> Cancel
        </Button>
        <Button size="sm" onClick={save} disabled={saving} className="h-7 bg-clay text-xs text-white hover:bg-clay-dark" data-testid="enrolment-edit-save">
          <Save className="mr-1 h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// VENDOR FORM DIALOG — sub-dialog opened from VendorsEditor.
// Supports "Copy from existing vendor" in Add mode so admin can quickly
// clone a known vendor's full address + contact and only flip category
// (useful when the same shopkeeper supplies multiple commodity types).
// ============================================================
function VendorFormDialog({ open, mode, initial, schoolId, copySources, onClose, onSaved }) {
  const [form, setForm] = useState(VENDOR_EMPTY);
  const [saving, setSaving] = useState(false);
  const [copyFromId, setCopyFromId] = useState("");

  useEffect(() => {
    if (open) {
      setForm(mode === "edit" && initial ? { ...VENDOR_EMPTY, ...initial } : VENDOR_EMPTY);
      setCopyFromId("");
    }
  }, [open, mode, initial]);

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleCopyFrom = (vid) => {
    setCopyFromId(vid);
    if (!vid) return;
    const src = (copySources || []).find((v) => v.id === vid);
    if (!src) return;
    // Clone every editable field from the source vendor; intentionally
    // strip id / createdAt / updatedAt / schoolId so this becomes a fresh
    // record on save. Admin typically only tweaks `category` afterwards.
    const { id, createdAt, updatedAt, schoolId: _sid, schoolIds: _sids, ...rest } = src;
    setForm({ ...VENDOR_EMPTY, ...rest });
    toast.success(`Copied from ${src.name || "vendor"} — now change category & save`);
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Vendor Name (English) is required"); return; }
    if (!form.nameOdia?.trim()) { toast.error("ବ୍ୟବସାୟୀଙ୍କ ନାମ (ଓଡ଼ିଆ) ବାଧ୍ୟତାମୂଳକ"); return; }
    if (!form.fatherName?.trim()) { toast.error(`${form.relationType === "husband" ? "Husband" : "Father"}'s Name (English) is required`); return; }
    if (!form.fatherNameOdia?.trim()) { toast.error(form.relationType === "husband" ? "ସ୍ୱାମୀଙ୍କ ନାମ (ଓଡ଼ିଆ) ବାଧ୍ୟତାମୂଳକ" : "ପିତାଙ୍କ ନାମ (ଓଡ଼ିଆ) ବାଧ୍ୟତାମୂଳକ"); return; }
    if (!form.village?.trim() || !form.villageOdia?.trim()) { toast.error("Village (English & ଓଡ଼ିଆ) is required"); return; }
    if (!form.post?.trim() || !form.postOdia?.trim()) { toast.error("Post (English & ଓଡ଼ିଆ) is required"); return; }
    if (!form.block?.trim() || !form.blockOdia?.trim()) { toast.error("Block (English & ଓଡ଼ିଆ) is required"); return; }
    if (!form.district?.trim() || !form.districtOdia?.trim()) { toast.error("District (English & ଓଡ଼ିଆ) is required"); return; }
    if (!form.pincode?.trim()) { toast.error("Pincode is required"); return; }
    setSaving(true);
    try {
      if (mode === "edit" && initial?.id) {
        // Merge existing record + edits to preserve any backend-only fields.
        const payload = { ...initial, ...form };
        await axios.post(`${API}/vendors?id=${encodeURIComponent(initial.id)}`, payload);
        toast.success("Vendor updated");
      } else {
        await axios.post(`${API}/vendors`, { ...form, schoolId, schoolIds: [schoolId] });
        toast.success("Vendor added");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save vendor");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="vendor-form-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? "Edit Vendor" : "Add Vendor"}</DialogTitle>
          <DialogDescription>Fill the full vendor details — English and ଓଡ଼ିଆ entries are paired.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {mode === "add" && (copySources || []).length > 0 && (
            <div className="sm:col-span-2 rounded-lg border border-dashed border-clay/40 bg-clay/5 p-3" data-testid="vendor-form-copy-row">
              <FieldLabel>📋 Copy from existing vendor (then just change category)</FieldLabel>
              <Select value={copyFromId} onValueChange={handleCopyFrom}>
                <SelectTrigger className="h-8 text-xs" data-testid="vendor-form-copy-select">
                  <SelectValue placeholder="— Pick an existing vendor to copy —" />
                </SelectTrigger>
                <SelectContent>
                  {(copySources || []).map((v) => (
                    <SelectItem key={v.id} value={v.id} data-testid={`vendor-form-copy-option-${v.id}`}>
                      {v.name || "—"} · {v.category || "—"} · {v.village || "—"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-[0.65rem] text-ink-700">
                Tip: if the same person supplies both grocery and vegetables, copy their existing record and only change the category before saving.
              </p>
            </div>
          )}
          <PairField label="Vendor Name" required
            valueEn={form.name} valueOdia={form.nameOdia}
            onChangeEn={upd("name")} onChangeOdia={upd("nameOdia")}
            testIdEn="vendor-form-name" testIdOdia="vendor-form-nameOdia" />
          <div>
            <FieldLabel>Category <span className="text-rose-600">*</span></FieldLabel>
            <Select value={form.category} onValueChange={upd("category")}>
              <SelectTrigger className="h-8 text-xs" data-testid="vendor-form-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {VENDOR_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <FieldLabel>Phone</FieldLabel>
            <Input value={form.phone || ""} onChange={(e) => upd("phone")(e.target.value)} className="h-8 text-xs" data-testid="vendor-form-phone" />
          </div>
          <PairField label={`${form.relationType === "husband" ? "Husband" : "Father"}'s Name`} required
            valueEn={form.fatherName} valueOdia={form.fatherNameOdia}
            onChangeEn={upd("fatherName")} onChangeOdia={upd("fatherNameOdia")}
            testIdEn="vendor-form-fatherName" testIdOdia="vendor-form-fatherNameOdia" />
          {/* iter153 — Relation picker gates the honorific below. Hidden for agency. */}
          {form.entityType !== "agency" && (
            <div className="sm:col-span-2">
              <FieldLabel>Relation <span className="text-rose-600">*</span></FieldLabel>
              <RelationPicker value={form.relationType} onChange={upd("relationType")} testIdPrefix="vendor-form" />
            </div>
          )}
          <PairField label="Village" required
            valueEn={form.village} valueOdia={form.villageOdia}
            onChangeEn={upd("village")} onChangeOdia={upd("villageOdia")}
            testIdEn="vendor-form-village" testIdOdia="vendor-form-villageOdia" />
          <PairField label="Post" required
            valueEn={form.post} valueOdia={form.postOdia}
            onChangeEn={upd("post")} onChangeOdia={upd("postOdia")}
            testIdEn="vendor-form-post" testIdOdia="vendor-form-postOdia" />
          <PairField label="Thana / Police Station"
            valueEn={form.thana} valueOdia={form.thanaOdia}
            onChangeEn={upd("thana")} onChangeOdia={upd("thanaOdia")}
            testIdEn="vendor-form-thana" testIdOdia="vendor-form-thanaOdia" />
          <PairField label="Block" required
            valueEn={form.block} valueOdia={form.blockOdia}
            onChangeEn={upd("block")} onChangeOdia={upd("blockOdia")}
            testIdEn="vendor-form-block" testIdOdia="vendor-form-blockOdia" />
          <PairField label="District" required
            valueEn={form.district} valueOdia={form.districtOdia}
            onChangeEn={upd("district")} onChangeOdia={upd("districtOdia")}
            testIdEn="vendor-form-district" testIdOdia="vendor-form-districtOdia" />
          <PairField label="Pincode" required
            valueEn={form.pincode} valueOdia={form.pincodeOdia}
            onChangeEn={upd("pincode")} onChangeOdia={upd("pincodeOdia")}
            testIdEn="vendor-form-pincode" testIdOdia="vendor-form-pincodeOdia" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="vendor-form-cancel">
            <X className="mr-1 h-3.5 w-3.5" /> Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-clay text-white hover:bg-clay-dark" data-testid="vendor-form-save">
            <Save className="mr-1 h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// VENDORS EDITOR — read-only table + sub-dialog for add/edit.
// ============================================================
export function VendorsEditor({ details, onCancel, onSaved }) {
  const schoolId = details?.profile?.id || "";
  const vendors = details?.vendors || [];
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add"); // "add" | "edit"
  const [formInitial, setFormInitial] = useState(null);

  const openAdd = () => { setFormMode("add"); setFormInitial(null); setFormOpen(true); };
  const openEdit = (v) => { setFormMode("edit"); setFormInitial(v); setFormOpen(true); };

  const remove = async (v) => {
    if (!confirm(`Delete vendor "${v.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/vendors/${v.id}`);
      toast.success("Vendor deleted");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete vendor");
    }
  };

  return (
    <div className="overflow-hidden rounded-lg border border-clay/40 bg-white" data-testid="vendors-editor">
      <table className="w-full text-xs">
        <thead className="bg-sand-100 text-[0.65rem] uppercase tracking-wide text-ink-700">
          <tr>
            <th className="px-2 py-1.5 text-left">Name · ନାମ</th>
            <th className="px-2 py-1.5 text-left">Category</th>
            <th className="px-2 py-1.5 text-left">Village / Post</th>
            <th className="px-2 py-1.5 text-left">Phone</th>
            <th className="px-2 py-1.5 text-left w-20">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {vendors.map((v) => (
            <tr key={v.id} data-testid={`vendor-row-${v.id}`}>
              <td className="px-2 py-1.5">
                <p className="font-medium">{v.name || <span className="italic text-rose-500">—</span>}</p>
                {v.nameOdia && <p className="text-[0.7rem] text-ink-700 font-[var(--font-odia)]">{v.nameOdia}</p>}
              </td>
              <td className="px-2 py-1.5 capitalize">{v.category || "—"}</td>
              <td className="px-2 py-1.5">{[v.village, v.post].filter(Boolean).join(", ") || "—"}</td>
              <td className="px-2 py-1.5">{v.phone || "—"}</td>
              <td className="px-2 py-1.5">
                <div className="flex gap-1">
                  <button type="button" onClick={() => openEdit(v)} className="rounded p-1 text-clay hover:bg-clay/10" title="Edit" data-testid={`vendor-row-${v.id}-edit`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => remove(v)} className="rounded p-1 text-rose-600 hover:bg-rose-50" title="Delete" data-testid={`vendor-row-${v.id}-delete`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {vendors.length === 0 && (
            <tr><td colSpan={5} className="px-3 py-3 text-center italic text-ink-700">No vendors yet — click "+ Add vendor" below.</td></tr>
          )}
        </tbody>
      </table>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-sand-50 px-3 py-2">
        <Button size="sm" variant="outline" onClick={openAdd} className="h-7 text-xs" data-testid="vendors-add-btn">
          <Plus className="mr-1 h-3.5 w-3.5" /> Add vendor
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} className="h-7 text-xs" data-testid="vendors-edit-done">
          <X className="mr-1 h-3.5 w-3.5" /> Done
        </Button>
      </div>
      <VendorFormDialog
        open={formOpen}
        mode={formMode}
        initial={formInitial}
        schoolId={schoolId}
        copySources={vendors}
        onClose={() => setFormOpen(false)}
        onSaved={() => onSaved?.()}
      />
    </div>
  );
}

// ============================================================
// CCH FORM DIALOG — sub-dialog opened from CchsEditor.
// Supports "Copy from existing CCH" in Add mode (handy when adding a
// second cook at the same school — same village/post/bank-branch — and
// the admin only needs to change name, gender, and category).
// ============================================================
function CchFormDialog({ open, mode, initial, schoolId, copySources, onClose, onSaved }) {
  const [form, setForm] = useState(CCH_EMPTY);
  const [saving, setSaving] = useState(false);
  const [copyFromId, setCopyFromId] = useState("");

  useEffect(() => {
    if (open) {
      setForm(mode === "edit" && initial ? { ...CCH_EMPTY, ...initial } : CCH_EMPTY);
      setCopyFromId("");
    }
  }, [open, mode, initial]);

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleCopyFrom = (cid) => {
    setCopyFromId(cid);
    if (!cid) return;
    const src = (copySources || []).find((c) => c.id === cid);
    if (!src) return;
    // Clone every editable field; strip id/timestamps/school link.
    const { id, createdAt, updatedAt, schoolId: _sid, schoolIds: _sids, ...rest } = src;
    setForm({ ...CCH_EMPTY, ...rest });
    toast.success(`Copied from ${src.name || "cook"} — now change name, gender & category`);
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("CCH Name (English) is required"); return; }
    if (!form.nameOdia?.trim()) { toast.error("CCH Name (ଓଡ଼ିଆ) is required"); return; }
    if (!form.gender) { toast.error("Gender is required"); return; }
    if (!form.category) { toast.error("Category is required"); return; }
    if (!form.fatherName?.trim() || !form.fatherNameOdia?.trim()) { toast.error(`${form.relationType === "husband" ? "Husband" : "Father"}'s Name (English & ଓଡ଼ିଆ) is required`); return; }
    if (!form.village?.trim() || !form.villageOdia?.trim()) { toast.error("Village (English & ଓଡ଼ିଆ) is required"); return; }
    if (!form.post?.trim() || !form.postOdia?.trim()) { toast.error("Post (English & ଓଡ଼ିଆ) is required"); return; }
    if (!form.thana?.trim() || !form.thanaOdia?.trim()) { toast.error("Thana (English & ଓଡ଼ିଆ) is required"); return; }
    if (!form.block?.trim() || !form.blockOdia?.trim()) { toast.error("Block (English & ଓଡ଼ିଆ) is required"); return; }
    if (!form.district?.trim() || !form.districtOdia?.trim()) { toast.error("District (English & ଓଡ଼ିଆ) is required"); return; }
    if (!form.pincode?.trim()) { toast.error("Pincode is required"); return; }
    if (!(Number(form.monthlyHonorarium) > 0)) { toast.error("Monthly Honorarium is required"); return; }
    if (!form.defaultMode) { toast.error("Default Payment Mode is required"); return; }
    setSaving(true);
    try {
      if (mode === "edit" && initial?.id) {
        const payload = { ...initial, ...form };
        await axios.post(`${API}/cchs?id=${encodeURIComponent(initial.id)}`, payload);
        toast.success("CCH updated");
      } else {
        await axios.post(`${API}/cchs`, { ...form, schoolId, schoolIds: [schoolId] });
        toast.success("CCH added");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save CCH");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="cch-form-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? "Edit Cook-cum-Helper" : "Add Cook-cum-Helper"}</DialogTitle>
          <DialogDescription>Fill the full CCH details — English and ଓଡ଼ିଆ entries are paired.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {mode === "add" && (copySources || []).length > 0 && (
            <div className="sm:col-span-2 rounded-lg border border-dashed border-clay/40 bg-clay/5 p-3" data-testid="cch-form-copy-row">
              <FieldLabel>📋 Copy from existing CCH (then change name, gender & category)</FieldLabel>
              <Select value={copyFromId} onValueChange={handleCopyFrom}>
                <SelectTrigger className="h-8 text-xs" data-testid="cch-form-copy-select">
                  <SelectValue placeholder="— Pick an existing cook to copy —" />
                </SelectTrigger>
                <SelectContent>
                  {(copySources || []).map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`cch-form-copy-option-${c.id}`}>
                      {c.name || "—"} · {c.gender || "—"} · {c.category || "—"} · {c.village || "—"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-[0.65rem] text-ink-700">
                Tip: same village/post/bank branch? Copy the existing cook and just change name, gender, and category before saving.
              </p>
            </div>
          )}
          <PairField label="CCH Name" required
            valueEn={form.name} valueOdia={form.nameOdia}
            onChangeEn={upd("name")} onChangeOdia={upd("nameOdia")}
            testIdEn="cch-form-name" testIdOdia="cch-form-nameOdia" />
          <div>
            <FieldLabel>Gender <span className="text-rose-600">*</span></FieldLabel>
            <Select value={form.gender || ""} onValueChange={upd("gender")}>
              <SelectTrigger className="h-8 text-xs" data-testid="cch-form-gender"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {CCH_GENDERS.filter((g) => g.value).map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <FieldLabel>Category <span className="text-rose-600">*</span></FieldLabel>
            <Select value={form.category || ""} onValueChange={upd("category")}>
              <SelectTrigger className="h-8 text-xs" data-testid="cch-form-category"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {CCH_CATEGORIES.filter((c) => c.value).map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <PairField label={`${form.relationType === "husband" ? "Husband" : "Father"}'s Name`} required
            valueEn={form.fatherName} valueOdia={form.fatherNameOdia}
            onChangeEn={upd("fatherName")} onChangeOdia={upd("fatherNameOdia")}
            testIdEn="cch-form-fatherName" testIdOdia="cch-form-fatherNameOdia" />
          {/* iter153 — Relation picker. */}
          {form.entityType !== "agency" && (
            <div className="sm:col-span-2">
              <FieldLabel>Relation <span className="text-rose-600">*</span></FieldLabel>
              <RelationPicker value={form.relationType} onChange={upd("relationType")} testIdPrefix="cch-form" />
            </div>
          )}
          <PairField label="Village" required
            valueEn={form.village} valueOdia={form.villageOdia}
            onChangeEn={upd("village")} onChangeOdia={upd("villageOdia")}
            testIdEn="cch-form-village" testIdOdia="cch-form-villageOdia" />
          <PairField label="Post" required
            valueEn={form.post} valueOdia={form.postOdia}
            onChangeEn={upd("post")} onChangeOdia={upd("postOdia")}
            testIdEn="cch-form-post" testIdOdia="cch-form-postOdia" />
          <PairField label="Thana / Police Station" required
            valueEn={form.thana} valueOdia={form.thanaOdia}
            onChangeEn={upd("thana")} onChangeOdia={upd("thanaOdia")}
            testIdEn="cch-form-thana" testIdOdia="cch-form-thanaOdia" />
          <PairField label="Block" required
            valueEn={form.block} valueOdia={form.blockOdia}
            onChangeEn={upd("block")} onChangeOdia={upd("blockOdia")}
            testIdEn="cch-form-block" testIdOdia="cch-form-blockOdia" />
          <PairField label="District" required
            valueEn={form.district} valueOdia={form.districtOdia}
            onChangeEn={upd("district")} onChangeOdia={upd("districtOdia")}
            testIdEn="cch-form-district" testIdOdia="cch-form-districtOdia" />
          <PairField label="Pincode" required
            valueEn={form.pincode} valueOdia={form.pincodeOdia}
            onChangeEn={upd("pincode")} onChangeOdia={upd("pincodeOdia")}
            testIdEn="cch-form-pincode" testIdOdia="cch-form-pincodeOdia" />
          <div>
            <FieldLabel>Bank Account No. (confidential)</FieldLabel>
            <Input value={form.bankAccount || ""} onChange={(e) => upd("bankAccount")(e.target.value)} className="h-8 font-mono text-xs" data-testid="cch-form-bankAccount" />
          </div>
          <div>
            <FieldLabel>IFSC Code (confidential)</FieldLabel>
            <Input value={form.ifsc || ""} onChange={(e) => upd("ifsc")(e.target.value)} className="h-8 font-mono text-xs" data-testid="cch-form-ifsc" />
          </div>
          <div>
            <FieldLabel>Phone</FieldLabel>
            <Input value={form.phone || ""} onChange={(e) => upd("phone")(e.target.value)} className="h-8 text-xs" data-testid="cch-form-phone" />
          </div>
          <div>
            <FieldLabel>Monthly Honorarium (₹) <span className="text-rose-600">*</span></FieldLabel>
            <Input
              type="number"
              min="0"
              value={form.monthlyHonorarium ?? 0}
              onChange={(e) => upd("monthlyHonorarium")(parseFloat(e.target.value) || 0)}
              className="h-8 text-xs"
              data-testid="cch-form-honorarium"
            />
          </div>
          <div>
            <FieldLabel>Default Payment Mode <span className="text-rose-600">*</span></FieldLabel>
            <Select value={form.defaultMode || "Bank"} onValueChange={upd("defaultMode")}>
              <SelectTrigger className="h-8 text-xs" data-testid="cch-form-defaultMode"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CCH_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="cch-form-cancel">
            <X className="mr-1 h-3.5 w-3.5" /> Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-clay text-white hover:bg-clay-dark" data-testid="cch-form-save">
            <Save className="mr-1 h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// CCHS EDITOR — read-only table + sub-dialog for add/edit.
// ============================================================
export function CchsEditor({ details, onCancel, onSaved }) {
  const schoolId = details?.profile?.id || "";
  const cchs = details?.cchs || [];
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formInitial, setFormInitial] = useState(null);

  const openAdd = () => { setFormMode("add"); setFormInitial(null); setFormOpen(true); };
  const openEdit = (c) => { setFormMode("edit"); setFormInitial(c); setFormOpen(true); };

  const remove = async (c) => {
    if (!confirm(`Delete CCH "${c.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/cchs/${c.id}`);
      toast.success("CCH deleted");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete CCH");
    }
  };

  return (
    <div className="overflow-hidden rounded-lg border border-clay/40 bg-white" data-testid="cchs-editor">
      <table className="w-full text-xs">
        <thead className="bg-sand-100 text-[0.65rem] uppercase tracking-wide text-ink-700">
          <tr>
            <th className="px-2 py-1.5 text-left">Name · ନାମ</th>
            <th className="px-2 py-1.5 text-left">Gender</th>
            <th className="px-2 py-1.5 text-left">Category</th>
            <th className="px-2 py-1.5 text-left">Phone</th>
            <th className="px-2 py-1.5 text-left">Mode</th>
            <th className="px-2 py-1.5 text-left w-20">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {cchs.map((c) => (
            <tr key={c.id} data-testid={`cch-row-${c.id}`}>
              <td className="px-2 py-1.5">
                <p className="font-medium">{c.name || <span className="italic text-rose-500">—</span>}</p>
                {c.nameOdia && <p className="text-[0.7rem] text-ink-700 font-[var(--font-odia)]">{c.nameOdia}</p>}
              </td>
              <td className="px-2 py-1.5">{c.gender || "—"}</td>
              <td className="px-2 py-1.5">{c.category || "—"}</td>
              <td className="px-2 py-1.5">{c.phone || "—"}</td>
              <td className="px-2 py-1.5"><Wallet className="inline h-3 w-3" /> {c.defaultMode || "—"}</td>
              <td className="px-2 py-1.5">
                <div className="flex gap-1">
                  <button type="button" onClick={() => openEdit(c)} className="rounded p-1 text-clay hover:bg-clay/10" title="Edit" data-testid={`cch-row-${c.id}-edit`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => remove(c)} className="rounded p-1 text-rose-600 hover:bg-rose-50" title="Delete" data-testid={`cch-row-${c.id}-delete`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {cchs.length === 0 && (
            <tr><td colSpan={6} className="px-3 py-3 text-center italic text-ink-700">No cook-cum-helpers yet — click "+ Add CCH" below.</td></tr>
          )}
        </tbody>
      </table>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-sand-50 px-3 py-2">
        <Button size="sm" variant="outline" onClick={openAdd} className="h-7 text-xs" data-testid="cchs-add-btn">
          <Plus className="mr-1 h-3.5 w-3.5" /> Add CCH
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} className="h-7 text-xs" data-testid="cchs-edit-done">
          <X className="mr-1 h-3.5 w-3.5" /> Done
        </Button>
      </div>
      <CchFormDialog
        open={formOpen}
        mode={formMode}
        initial={formInitial}
        schoolId={schoolId}
        copySources={cchs}
        onClose={() => setFormOpen(false)}
        onSaved={() => onSaved?.()}
      />
    </div>
  );
}
